import type { ReactNode } from 'react';

/**
 * The shared page header for every top-level view. A small rounded accent mark
 * (the agent's initial) sits left of the title, tinted with that agent's accent
 * token, so each page carries a consistent-but-distinct identity. Utility pages
 * use the `primary` accent.
 *
 * Accent classes are passed as complete literals (not interpolated) so Tailwind's
 * JIT always sees them.
 */
export interface PageHeaderProps {
  /** Single letter/emoji shown in the accent mark. */
  mark: string;
  title: string;
  subtitle?: ReactNode;
  /** bg tint for the mark, e.g. 'bg-hermes/12'. */
  markBg: string;
  /** text color for the mark, e.g. 'text-hermes'. */
  markText: string;
  /** trailing content rendered inline with the title (e.g. a status chip). */
  actions?: ReactNode;
  className?: string;
}

export function PageHeader({
  mark,
  title,
  subtitle,
  markBg,
  markText,
  actions,
  className = '',
}: PageHeaderProps) {
  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex items-center gap-3">
        <span
          className={`w-9 h-9 rounded-xl flex items-center justify-center text-base font-bold shrink-0 ${markBg} ${markText}`}
        >
          {mark}
        </span>
        <h1 className="text-2xl font-bold">{title}</h1>
        {actions}
      </div>
      {subtitle && <p className="text-gray-500 mt-2">{subtitle}</p>}
    </div>
  );
}
